import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/context/AuthContext';
import RichTextEditor from '@/components/portal/RichTextEditor';
import {
  FilePlus2,
  Loader2,
  Trash2,
  Pencil,
  Save,
  X,
  Search,
  Eye,
  EyeOff,
  FileText,
} from 'lucide-react';

const ROLES = ['admin', 'manager', 'agent', 'employee'];

const emptyForm = {
  id: null,
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  visible_roles: ['employee'],
  is_published: true,
};

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // null | emptyForm | existing page
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pages`);
      setPages(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = pages.filter((p) => {
    const s = q.trim().toLowerCase();
    return !s || p.title.toLowerCase().includes(s) || p.slug.toLowerCase().includes(s);
  });

  const openCreate = () => setEditing({ ...emptyForm });
  const openEdit = (p) => setEditing({ ...p });

  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim()) return window.alert('Title is required');
    if (!editing.visible_roles?.length) return window.alert('Assign at least one role');
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        slug: editing.slug || undefined,
        excerpt: editing.excerpt || '',
        content: editing.content || '',
        visible_roles: editing.visible_roles,
        is_published: editing.is_published,
      };
      if (editing.id) {
        const { data } = await axios.patch(`${API}/admin/pages/${editing.id}`, payload);
        setPages((all) => all.map((p) => (p.id === data.id ? data : p)));
      } else {
        const { data } = await axios.post(`${API}/admin/pages`, payload);
        setPages((all) => [data, ...all]);
      }
      close();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await axios.delete(`${API}/admin/pages/${id}`);
      setPages((all) => all.filter((p) => p.id !== id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleRole = (r) => {
    setEditing((cur) => {
      const set = new Set(cur.visible_roles);
      if (set.has(r)) set.delete(r);
      else set.add(r);
      return { ...cur, visible_roles: Array.from(set) };
    });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Pages</h1>
          <p className="text-slate-600 text-sm">Create internal pages and control which roles can view them.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-white font-semibold hover:brightness-110 transition"
          style={{ backgroundColor: '#1e73be' }}
        >
          <FilePlus2 className="w-4 h-4" /> New Page
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-slate-100">
          <div className="flex items-center border border-slate-200 rounded-md overflow-hidden focus-within:border-[#1e73be]">
            <span className="px-3 h-9 grid place-items-center text-slate-400"><Search className="w-4 h-4" /></span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search pages"
              className="flex-1 h-9 outline-none text-sm"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : err ? (
          <div className="p-8 text-center text-red-600">{err}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No pages yet</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((p) => (
              <li key={p.id} className="px-4 py-3 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#eaf3fb] text-[#1e73be] flex-none">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{p.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                    <span className="font-mono truncate">/portal/pages/{p.slug}</span>
                    <span>&middot;</span>
                    <span>Updated {new Date(p.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="hidden md:flex flex-wrap gap-1 justify-end max-w-[240px]">
                  {p.visible_roles.map((r) => (
                    <span key={r} className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">
                      {r}
                    </span>
                  ))}
                </div>
                <span className="hidden sm:inline text-xs font-semibold" title={p.is_published ? 'Published' : 'Draft'}>
                  {p.is_published ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                </span>
                <button onClick={() => openEdit(p)} className="w-9 h-9 grid place-items-center rounded-md text-slate-600 hover:bg-slate-100" title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(p.id)} className="w-9 h-9 grid place-items-center rounded-md text-red-700 hover:bg-red-50" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
          <div className="bg-white rounded-xl w-full max-w-[900px] my-6" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold text-slate-900">
                {editing.id ? 'Edit page' : 'New page'}
              </h3>
              <button onClick={close} className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-100 text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-slate-700 text-[13px] font-semibold mb-1.5">Title</label>
                  <input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="e.g. Home Loan SOP"
                    className="w-full h-11 px-3 border border-slate-200 rounded-md outline-none focus:border-[#1e73be]"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 text-[13px] font-semibold mb-1.5">Slug (optional)</label>
                  <input
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="auto-from-title"
                    className="w-full h-11 px-3 border border-slate-200 rounded-md outline-none focus:border-[#1e73be]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-700 text-[13px] font-semibold mb-1.5">Excerpt</label>
                <input
                  value={editing.excerpt}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  placeholder="Short description shown in the list"
                  className="w-full h-11 px-3 border border-slate-200 rounded-md outline-none focus:border-[#1e73be]"
                />
              </div>

              <div>
                <label className="block text-slate-700 text-[13px] font-semibold mb-1.5">Content</label>
                <RichTextEditor
                  value={editing.content}
                  onChange={(html) => setEditing((cur) => ({ ...cur, content: html }))}
                />
              </div>

              <div>
                <label className="block text-slate-700 text-[13px] font-semibold mb-2">Visible to roles</label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((r) => {
                    const active = editing.visible_roles?.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => toggleRole(r)}
                        className={`px-3 h-9 rounded-full text-sm font-medium border transition ${
                          active
                            ? 'bg-[#1e73be] text-white border-[#1e73be]'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-[#1e73be]/40'
                        }`}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Admins always see all pages. Only the selected roles will see this page in their dashboard.
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={editing.is_published}
                  onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                  className="w-4 h-4 accent-[#1e73be]"
                />
                Published (visible to users)
              </label>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={close} className="px-4 h-10 rounded-md border border-slate-200 text-slate-700 font-medium">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-white font-semibold disabled:opacity-70"
                style={{ backgroundColor: '#1e73be' }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving...' : editing.id ? 'Save changes' : 'Create page'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPages;
